"use client";
import { useState } from "react";
import ShoppingCart from "lucide-react/dist/esm/icons/shopping-cart";
import Heart from "lucide-react/dist/esm/icons/heart";
import Download from "lucide-react/dist/esm/icons/download";
import Zap from "lucide-react/dist/esm/icons/zap";
import HardDrive from "lucide-react/dist/esm/icons/hard-drive";
import TrendingUp from "lucide-react/dist/esm/icons/trending-up";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { allowsFreeClaim, isPwywAsset, pwywMinimum, resolvePwywAmount } from "@/lib/pwyw";
import { listingPrice } from "@/lib/listing-price";
import { AssetPrice } from "@/components/asset/AssetPrice";
import { DiscountBadge } from "@/components/asset/DiscountBadge";
import { cn } from "@/lib/utils";
import type { AssetDetail } from "@/hooks/useAssetDetail";
import { formatFileSize } from "@/lib/format";
import { SignUpFlowDialog } from "@/components/auth/SignUpFlowDialog";
import { RatingPromptDialog } from "@/components/asset-detail/RatingPromptDialog";
import { TipDialog } from "@/components/asset-detail/TipDialog";
import { LicenseAgreementDialog } from "@/components/asset-detail/LicenseAgreementDialog";
import dynamic from "next/dynamic";
import { useAssetActions } from "@/hooks/useAssetActions";
import { useRecentDownloads } from "@/hooks/useRecentDownloads";
import { useAssetPurchaseStats } from "@/hooks/useAssetPurchaseStats";
import { DailyDownloadBadge } from "@/components/asset-detail/DailyDownloadBadge";
import { VatInclusiveNote } from "@/components/pricing/VatInclusiveNote";
import { useCurrency } from "@/contexts/CurrencyContext";

// Paywall dialog only loads when a free-tier user actually hits a limit moment.
const DownloadLimitDialog = dynamic(
  () => import("@/components/asset-detail/DownloadLimitDialog"),
  { ssr: false },
);

interface MobilePurchaseBarProps {
  asset: AssetDetail;
}

const HOT_DOWNLOAD_THRESHOLD = 10;

export function MobilePurchaseBar({ asset }: MobilePurchaseBarProps) {
  const [pwywAmount, setPwywAmount] = useState("");
  // Display currency for fixed prices (via AssetPrice); PWYW amounts are
  // typed/charged in USD.
  const { formatUsd } = useCurrency();

  const isPwyw = isPwywAsset(asset);
  const pwywMin = pwywMinimum(asset);
  const pwywFree = allowsFreeClaim(asset);
  const pwywState = resolvePwywAmount(asset, pwywAmount);

  // What a buyer pays now: 0 free, sale-aware for fixed, the minimum for PWYW.
  const effectivePrice = listingPrice(asset).amount;

  const actions = useAssetActions({ asset, effectivePrice });

  const { data: recentDownloads } = useRecentDownloads(asset.id);
  const { data: purchaseStats } = useAssetPurchaseStats(asset.id);

  const isHot = (recentDownloads ?? 0) >= HOT_DOWNLOAD_THRESHOLD;


  const {
    access,
    isLoadingAccess,
    isAddingToCart,
    isBuyingNow,
    isDownloading,
    isInWishlist,
    isTogglingWishlist,
    showDownloadButton,
    showFreeDownloadButton,
    showPurchaseButtons,
    showTipButton,
    addToCart,
    buyNow,
    toggleWishlist,
    openDownloadDialog,
    confirmDownload,
    requestTip,
    handleExpressSuccess,
    showLicenseDialog,
    setShowLicenseDialog,
    licenseAgreed,
    setLicenseAgreed,
    showExpressSignUp,
    setShowExpressSignUp,
    signUpIntent,
    showRatingPrompt,
    setShowRatingPrompt,
    recommendations,
    showTipDialog,
    setShowTipDialog,
    limitDialogVariant,
    setLimitDialogVariant,
    dailyDownload,
  } = actions;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t lg:hidden min-h-14" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
      {/* Social proof strip */}
      {(purchaseStats?.purchases_last_24h ?? 0) > 0 ? (
        <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning bg-warning/10 px-3 py-1.5 border-b border-warning/20">
          <TrendingUp className="h-3 w-3" />
          🔥 {purchaseStats?.purchases_last_24h} bought in the last 24h
        </div>
      ) : (purchaseStats?.purchases_last_7d ?? 0) > 0 ? (
        <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning bg-warning/10 px-3 py-1.5 border-b border-warning/20">
          <TrendingUp className="h-3 w-3" />
          {purchaseStats?.purchases_last_7d} purchases this week
        </div>
      ) : isHot ? (
        <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning bg-warning/10 px-3 py-1.5 border-b border-warning/20">
          <TrendingUp className="h-3 w-3" />
          {recentDownloads} downloads in the last 30 days
        </div>
      ) : null}

      <div className="container mx-auto flex items-center gap-3 p-3">
        {/* Price and File Size */}
        <div className="shrink-0">
          <div className="flex flex-col items-start gap-0.5">
            <DiscountBadge asset={asset} />
            {/* PWYW wraps onto two short lines so the buttons keep their room. */}
            <AssetPrice
              asset={asset}
              className={cn("font-bold", isPwyw ? "max-w-[5rem] text-sm" : "text-lg")}
            />
          </div>
          {asset.file_size && !isPwyw && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <HardDrive className="h-3 w-3" />
              {formatFileSize(asset.file_size)}
            </div>
          )}
          {!asset.is_free && !isPwyw && effectivePrice > 0 && (
            <VatInclusiveNote net={effectivePrice} compact className="text-2xs leading-tight" />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-1 flex flex-col gap-1.5">
          {/* PWYW input inline */}
          {showPurchaseButtons && isPwyw && (
            <div className="flex gap-1.5">
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-medium">$</span>
                <Input
                  type="number"
                  min={pwywFree ? "0" : pwywMin}
                  step="0.01"
                  placeholder={pwywFree ? "0.00 = free" : `Min ${formatUsd(pwywMin)}`}
                  value={pwywAmount}
                  onChange={(e) => setPwywAmount(e.target.value)}
                  aria-label="Pay what you want amount"
                  aria-invalid={pwywState.kind === "invalid"}
                  aria-describedby={pwywState.kind === "invalid" ? "pwyw-error-mobile" : undefined}
                  className="pl-5 h-8 text-sm"
                />
              </div>
            </div>
          )}

          {showPurchaseButtons && isPwyw && pwywState.kind === "invalid" && (
            <p id="pwyw-error-mobile" className="text-2xs text-destructive">
              {pwywState.message}
            </p>
          )}

          <div className="flex gap-1.5">
            {isLoadingAccess && (
              <Button className="flex-1" size="sm" loading loadingLabel="Checking access" />
            )}

            {!isLoadingAccess && showDownloadButton && (
              <Button
                className="flex-1 font-semibold"
                onClick={openDownloadDialog}
                size="sm"
                loading={isDownloading}
                loadingLabel="Preparing download"
              >
                {!isDownloading && <Download className="mr-1.5 h-3.5 w-3.5" />}
                {isDownloading ? "Preparing…" : "Download"}
              </Button>
            )}

            {!isLoadingAccess && showFreeDownloadButton && (
              <Button
                className="flex-1 bg-success text-success-foreground hover:bg-success/90 font-semibold"
                onClick={openDownloadDialog}
                size="sm"
                loading={isDownloading}
                loadingLabel="Preparing download"
              >
                {!isDownloading && <Download className="mr-1.5 h-3.5 w-3.5" />}
                {isDownloading ? "Preparing…" : "Download Free"}
              </Button>
            )}

            {!isLoadingAccess && showPurchaseButtons && !isPwyw && (
              <>
                <Button
                  className="flex-1 h-11 font-semibold"
                  onClick={() => buyNow()}
                  disabled={isAddingToCart}
                  loading={isBuyingNow}
                  loadingLabel="Processing checkout"
                >
                  {!isBuyingNow && <Zap className="mr-1.5 h-3.5 w-3.5" />}
                  {isBuyingNow ? "Processing…" : "Buy Now"}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 shrink-0"
                  onClick={() => addToCart()}
                  disabled={isBuyingNow}
                  aria-label="Add to cart"
                  loading={isAddingToCart}
                  loadingLabel="Adding to cart"
                >
                  {!isAddingToCart && <ShoppingCart className="h-3.5 w-3.5" />}
                </Button>
              </>
            )}

            {/* $0 on a no-minimum PWYW asset is a free claim, not a purchase. */}
            {!isLoadingAccess && showPurchaseButtons && isPwyw && pwywState.kind === "free" && (
              <Button
                className="flex-1 bg-success text-success-foreground hover:bg-success/90 font-semibold"
                onClick={openDownloadDialog}
                size="sm"
                loading={isDownloading}
                loadingLabel="Preparing download"
              >
                {!isDownloading && <Download className="mr-1.5 h-3.5 w-3.5" />}
                {isDownloading ? "Preparing…" : "Get it free"}
              </Button>
            )}

            {!isLoadingAccess && showPurchaseButtons && isPwyw && pwywState.kind !== "free" && (
              <Button
                className="flex-1 font-semibold"
                onClick={() => pwywState.kind === "paid" && buyNow(pwywState.amount)}
                disabled={isAddingToCart || pwywState.kind !== "paid"}
                size="sm"
                loading={isBuyingNow}
                loadingLabel="Processing checkout"
              >
                {!isBuyingNow && <Zap className="mr-1.5 h-3.5 w-3.5" />}
                {isBuyingNow
                  ? "Processing…"
                  : pwywState.kind === "paid"
                    ? `Buy — ${formatUsd(pwywState.amount)}`
                    : "Enter Amount"}
              </Button>
            )}

            {/* Tip button */}
            {!isLoadingAccess && showTipButton && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={requestTip}
                aria-label="Tip the creator"
              >
                <Heart className="h-3.5 w-3.5 fill-current" />
              </Button>
            )}
          </div>

          {/* Daily download counter — mobile parity with the desktop rail */}
          {(showDownloadButton || showFreeDownloadButton) && (
            <DailyDownloadBadge daily={dailyDownload} compact />
          )}
        </div>

        {/* Wishlist - only show if not owner */}
        {!access?.isOwner && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleWishlist}
            disabled={isTogglingWishlist}
            aria-pressed={isInWishlist}
            aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
            className="shrink-0"
          >
            <Heart className={`h-5 w-5 ${isInWishlist ? "fill-destructive text-destructive" : ""}`} />
          </Button>
        )}
      </div>

      {/* Express Sign-Up Dialog */}
      <SignUpFlowDialog
        open={showExpressSignUp}
        onOpenChange={setShowExpressSignUp}
        intent={signUpIntent}
        context={{
          title: asset.title,
          thumbnail: asset.images?.[0]?.image_url,
          isFree: asset.is_free,
          sellerId: asset.seller?.id,
        }}
        onSuccess={handleExpressSuccess}
      />

      {/* License Agreement Dialog (shared) */}
      <LicenseAgreementDialog
        open={showLicenseDialog}
        onOpenChange={setShowLicenseDialog}
        licenseType={asset.license_type}
        agreed={licenseAgreed}
        onAgreedChange={setLicenseAgreed}
        onConfirm={confirmDownload}
        idPrefix="mobileLicenseAgreement"
      />

      {/* Download-limit paywall / soft-wall interstitial */}
      {limitDialogVariant && (
        <DownloadLimitDialog
          open
          onOpenChange={(next) => { if (!next) setLimitDialogVariant(null); }}
          variant={limitDialogVariant}
          used={dailyDownload.used}
        />
      )}

      {/* Rating Prompt + post-download recommendations */}
      <RatingPromptDialog
        open={showRatingPrompt}
        onOpenChange={setShowRatingPrompt}
        assetId={asset.id}
        assetTitle={asset.title}
        recommendations={recommendations}
      />

      {/* Tip Dialog */}
      <TipDialog
        open={showTipDialog}
        onOpenChange={setShowTipDialog}
        assetId={asset.id}
        assetTitle={asset.title}
        sellerName={asset.seller?.display_name || asset.seller?.username || "Creator"}
      />
    </div>
  );
}
